/* eslint-disable react/no-children-prop */
import { useEffect, useState } from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import ReactMarkdown from "react-markdown";

import styles from "../../../styles/traits/element/ElementTraitsPage.module.css";

import { ExtendedScale } from "@/models/sharedModels";
import elementsJsonData from "../../../assets/traitInfo/elements.json";
import MobileSearchPage from "@/components/publicProfiles/Header/MobileSearchPage";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import ProfileHeader from "@/components/publicProfiles/Header/ProfileHeader";
import BreadCrumbs from "@/components/shared/breadCrumbs/BreadCrumbs";
import Histogram from "@/components/traits/detailsTab/histogram/Histogram";
import { traitsMapper } from "../../../components/traits/shared/traitsMap";
import ElementTraitsPageLeftBar from "@/components/traits/element/elementsLeftBar/ElementTraitsPageLeftBar";
import AboutThisTrait from "@/components/traits/detailsTab/aboutThisTrait/AboutThisTrait";
import OtherElements from "@/components/traits/detailsTab/elements/otherElements/OtherElements";
import OppositeElement from "@/components/traits/detailsTab/elements/opposite/OppositeElement";
import ChildElement from "@/components/traits/detailsTab/elements/child/ChildElement";
import ElementComponentOf from "@/components/traits/detailsTab/elements/componentOf/ElementComponentOf";
import Footers from "@/components/shared/Footers/Footers";
import TraitsSEOHeader from "@/components/traits/shared/TraitsSEOHeader";
import SwipeTabs from "@/components/shared/SwipeTabs/SwipeTabs";
import LargeTraitsHeading from "@/components/traits/shared/LargeTraitsHeading/LargeTraitsHeading";
import ParentOf from "@/components/traits/detailsTab/elements/parentOf/ParentOf";

type ElementTraitsPageProps = {
  element: ExtendedScale;
  otherElements: ExtendedScale[];
  tabs: string[];
  markdownContent: string[];
  slicedMarkdown: string[];
};

export default function ElementTraitsPage(props: ElementTraitsPageProps) {
  const { mobileSearchOpen } = usePublicProfileContext();
  const [dimensionSlug, setDimensionSlug] = useState<string>("");

  useEffect(() => {
    if (props.element.dimensionSlug) {
      if (props.element.dimensionSlug === "primary-traits") {
        setDimensionSlug("personality");
      } else if (props.element.dimensionSlug === "cognition") {
        setDimensionSlug("personality-II");
      } else if (props.element.dimensionSlug === "work-style") {
        setDimensionSlug("communication-style");
      } else {
        setDimensionSlug(props.element.dimensionSlug);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.element.slug]);

  return (
    <>
      <TraitsSEOHeader
        title={`Dimensional || ${props.element.name}`}
        description={props.element.shortDescription ?? props.element.name}
      />
      {mobileSearchOpen === true && <MobileSearchPage />}
      <ProfileHeader />
      <div className={styles.main}>
        <div className={styles.top}>
          {dimensionSlug !== "" ? (
            <BreadCrumbs
              list={[
                "Traits",
                "Elements",
                traitsMapper[props.element.dimensionSlug],
                props.element.name,
              ]}
              urls={[
                "/traits",
                "/traits/elements",
                `/dimensions/${dimensionSlug}`,
              ]}
            />
          ) : (
            <BreadCrumbs
              list={["Traits", "Elements", props.element.name]}
              urls={["/traits", "/traits/elements"]}
            />
          )}
          <LargeTraitsHeading
            title={props.element.alias ?? props.element.name}
            noMobile
          />
        </div>
        <div className={styles.parent}>
          <ElementTraitsPageLeftBar element={props.element} />
          <div className={styles.rightBarMain}>
            <SwipeTabs
              tabs={props.tabs}
              content={[
                <div key={1} className={styles.markdownDiv}>
                  <ReactMarkdown children={props.markdownContent[0]} />
                </div>,
                <div key={2}>
                  <AboutThisTrait
                    dimensionSlug={props.element.dimensionSlug}
                    traitContent={props.element.traitContent}
                  />
                  <Histogram
                    // @ts-ignore
                    data={props.element.traitContent.histogramData}
                  />
                  {props.otherElements && props.otherElements.length > 0 && (
                    <OtherElements elements={props.otherElements} />
                  )}
                  {props.element.traitContent.oppositeDetails?.oppositeTrait
                    .slug && <OppositeElement element={props.element} />}
                  {props.element.traitContent.childDetails && (
                    <ChildElement scale={props.element} authenticated />
                  )}
                  {props.element.traitContent.componentOfDetails && (
                    <ElementComponentOf scale={props.element} />
                  )}
                  {props.element.traitContent?.parentDetails?.childTraits
                    .length && <ParentOf element={props.element} />}
                </div>,
              ]}
              slicedContent={props.slicedMarkdown}
              urls={`/traits/elements/${props.element.slug}`}
            />
          </div>
        </div>
      </div>
      <Footers />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const list: string[] = [];
  elementsJsonData.forEach((el) => {
    if (el.isDiscoverable === true) list.push(el.slug);
  });
  const paths = list.map((el) => ({
    params: { slug: el },
  }));

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  let element: any;
  let slug: any;
  let otherSlugs: any[] = [];
  let otherElements: any[] = [];
  let tabs: string[] = ["identity", "Details"];
  let markdownContent: string[] = [];
  let slicedMarkdown: string[] = [];
  if (ctx?.params?.slug) {
    slug = ctx.params.slug;
  }

  if (slug) {
    elementsJsonData.forEach((el) => {
      if (el.slug === slug) {
        element = el;
        el.traitContent?.otherDetails?.otherTraits.forEach((otherArch) => {
          otherSlugs.push(otherArch.slug);
        });
      }
    });
  }

  if (element) {
    if (element.traitContent?.markdownSections?.length === 0) {
      element.traitContent.markdownSections.push({
        markdownString: `### Short Description\n\n${element.shortDescription}`,
        traitSection: "identity",
        title: "Identity",
        feedbackType: "accuracy",
      });
    }

    if (!element.traitContent.markdownSections) {
      element.traitContent.markdownSections = [
        {
          markdownString: `### Short Description\n\n${element.shortDescription}`,
          traitSection: "identity",
          title: "Identity",
          feedbackType: "accuracy",
        },
      ];
    }

    element.traitContent.markdownSections?.forEach(
      (section: any, index: number) => {
        if (!tabs.includes(section.traitSection as string)) {
          tabs.push(section.traitSection as string);
        }
        markdownContent.push(section.markdownString);
        if (index !== 0) {
          slicedMarkdown.push(section.markdownString);
        }
      }
    );
  }

  if (otherSlugs && otherSlugs.length > 0) {
    elementsJsonData.forEach((el) => {
      if (otherSlugs.includes(el.slug)) {
        otherElements.push(el);
      }
    });
  }

  if (!element) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      element: element,
      otherElements: otherElements,
      tabs: tabs,
      markdownContent: markdownContent,
      slicedMarkdown: slicedMarkdown,
    },
  };
};
